'use client';

import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { createClient } from '@/lib/supabase/client';

export default function ReviewSubmissionForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Please complete the reCAPTCHA verification before submitting.');
      return;
    }

    setLoading(true);

    try {
      const supabase = createClient();
      const { error: insertError } = await supabase.from('reviews').insert({
        reviewer_name: name.trim(),
        reviewer_email: email.trim() || null,
        rating,
        review_text: reviewText.trim(),
        status: 'pending',
      });

      if (insertError) {
        setError('Failed to submit your review. Please try again.');
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
        return;
      }

      setSubmitted(true);
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{
        background: 'rgba(185,152,95,0.08)',
        border: '1px solid rgba(185,152,95,0.3)',
        borderRadius: '12px',
        padding: '40px 32px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>✓</div>
        <h3 style={{ color: '#e8d5b0', fontSize: '20px', fontWeight: 600, marginBottom: '10px' }}>
          Thank You for Your Review
        </h3>
        <p style={{ color: '#a89070', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Your review has been submitted and is pending approval. We appreciate you taking the time to share your experience.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', color: '#a89070', fontSize: '13px', marginBottom: '8px', fontWeight: 500 }}>
            Full Name <span style={{ color: '#b9985f' }}>*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Your name"
            style={{
              width: '100%',
              padding: '11px 14px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(185,152,95,0.2)',
              borderRadius: '8px',
              color: '#e8d5b0',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', color: '#a89070', fontSize: '13px', marginBottom: '8px', fontWeight: 500 }}>
            Email <span style={{ color: '#7a6a55', fontSize: '12px' }}>(optional)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{
              width: '100%',
              padding: '11px 14px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(185,152,95,0.2)',
              borderRadius: '8px',
              color: '#e8d5b0',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      <div>
        <label style={{ display: 'block', color: '#a89070', fontSize: '13px', marginBottom: '10px', fontWeight: 500 }}>
          Rating <span style={{ color: '#b9985f' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '28px',
                color: star <= (hoveredStar || rating) ? '#b9985f' : '#3a3028',
                padding: '0 2px',
                transition: 'color 0.15s',
              }}
              aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            >
              ★
            </button>
          ))}
          <span style={{ color: '#7a6a55', fontSize: '13px', alignSelf: 'center', marginLeft: '8px' }}>
            {rating === 5 ? 'Excellent' : rating === 4 ? 'Very Good' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
          </span>
        </div>
      </div>

      <div>
        <label style={{ display: 'block', color: '#a89070', fontSize: '13px', marginBottom: '8px', fontWeight: 500 }}>
          Your Review <span style={{ color: '#b9985f' }}>*</span>
        </label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          required
          rows={4}
          placeholder="Share your experience with Harrington & Associates LLP…"
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(185,152,95,0.2)',
            borderRadius: '8px',
            color: '#e8d5b0',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            boxSizing: 'border-box',
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: '1.6',
          }}
        />
      </div>

      <div>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
          onChange={(token) => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
          theme="dark"
        />
      </div>

      {error && (
        <div style={{
          background: 'rgba(220,38,38,0.1)',
          border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: '8px',
          padding: '12px 14px',
          color: '#f87171',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        style={{
          padding: '13px 28px',
          background: loading ? 'rgba(185,152,95,0.4)' : '#b9985f',
          color: '#15120f',
          border: 'none',
          borderRadius: '8px',
          fontSize: '15px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          alignSelf: 'flex-start',
          transition: 'background 0.2s',
        }}
      >
        {loading ? 'Submitting…' : 'Submit Review'}
      </button>
    </form>
  );
}
